import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch, apiPut } from '@/api/axios-client'
import { draftToInput, timelineBody, type PlanDraft, type PlanResponse, type PlanView } from './model'

export const planKey = (projectId: string) => ['project', projectId, 'plan'] as const

export function useProjectPlan(projectId: string) {
  return useQuery({ queryKey: planKey(projectId), queryFn: () => apiGet<PlanResponse>(`project-plans/${projectId}`), enabled: Boolean(projectId) })
}

/** Plan fields, then the dates (which live with Project Timeline). */
export async function savePlan(projectId: string, draft: PlanDraft, version: number | null) {
  const res = await apiPut<{ plan: PlanView }>(`project-plans/${projectId}`, { ...draftToInput(draft), ...(version !== null ? { version } : {}) })
  const dates = timelineBody(draft)
  if (draft.startDate || draft.endDate) {
    // Timeline settings are versioned like everything else; send the version we are replacing.
    const current = await apiGet<{ version: number | null }>(`projects/${projectId}/timeline-settings`)
    await apiPatch(`projects/${projectId}/timeline-settings`, { ...dates, ...(current.version !== null ? { settingsVersion: current.version } : {}) })
  }
  return res.plan
}

export function useSavePlan(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, version }: { draft: PlanDraft; version: number | null }) => savePlan(projectId, draft, version),
    onSuccess: () => qc.invalidateQueries({ queryKey: planKey(projectId) }),
  })
}
